/**
 * Progressive enhancement for a statically rendered site.
 * Everything here is optional — the pages read and navigate without it.
 */

/* ---- mobile navigation ---- */
export function initNav() {
  const toggle = document.querySelector<HTMLButtonElement>("[data-nav-toggle]");
  const links = document.querySelector<HTMLElement>("[data-nav-links]");
  if (!toggle || !links) return;

  toggle.addEventListener("click", () => {
    const open = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", String(!open));
    links.classList.toggle("hidden", open);
    links.classList.toggle("flex", !open);
  });
}

/* ---- notation fields ---- */

export type Notehead = { x: number; y: number; kind: "square" | "diamond"; weight: 0 | 1 | 2 };
export type StaveField = { staves: number[]; notes: Notehead[] };

/**
 * Where a notation field puts its stave lines and noteheads.
 *
 * Mensural notation, which is what the company mark is: four ruled lines, and
 * square and diamond noteheads sitting on lines and in spaces. It replaced a
 * field of randomly filled square cells — that read as a loading skeleton or a
 * blurred screenshot rather than as anything intended, and it shared no
 * language with the marks.
 *
 * Still rectilinear, which is the constraint that matters: Microsaic Systems,
 * an unrelated company with a near-identical name, uses a circular cluster of
 * dots. Square noteheads on ruled lines are about as far from that as a mark
 * gets. The diamonds are period-correct and read as squares on their corner,
 * not as dots.
 *
 * Pure and deterministic, so a seed always gives the same field and the part
 * worth testing needs no canvas.
 *
 * `staves` holds the y of each stave's top line. `notes` are in absolute
 * coordinates, already snapped to a line or a space.
 */
export function staveField(
  seed: number,
  width: number,
  height: number,
  lineGap: number,
  density: number,
): StaveField {
  let state = seed || 1;
  const rnd = () => {
    state = (state * 1103515245 + 12345) % 2147483648;
    return state / 2147483648;
  };

  const staveHeight = lineGap * 3; // four lines
  // Two stave-gaps between systems. Three leaves a card-sized box with room for
  // only two staves and a third of it empty, which reads as a cropped image.
  const blockHeight = staveHeight + lineGap * 2;
  const staves: number[] = [];
  const notes: Notehead[] = [];

  // Half-steps: 0 is the top line, 1 the space under it, and so on, one step
  // above the stave and one below so a line can breathe without leaving the box.
  const STEPS = 9;
  const step = lineGap / 2;

  // A note may sit a half-step below the bottom line, and a notehead is drawn
  // from its centre, so a stave needs a full gap of clearance under it or the
  // lowest notes are sliced by the edge of the box.
  for (let top = lineGap; top + staveHeight + lineGap <= height; top += blockHeight) {
    staves.push(top);
    for (let x = lineGap * 1.5; x < width - lineGap; x += lineGap * 1.5) {
      if (rnd() > density) continue;
      const pitch = Math.floor(rnd() * STEPS) - 1;
      const r = rnd();
      notes.push({
        x,
        y: top + pitch * step,
        kind: r < 0.78 ? "square" : "diamond",
        weight: r < 0.55 ? 0 : r < 0.88 ? 1 : 2,
      });
    }
  }

  return { staves, notes };
}

/* ---- mosaic fields, standing in for photography ---- */
/**
 * Draw one notehead. A square sits on the grid; a diamond is the same square
 * on its corner, which is what mensural notation actually uses — not a circle.
 */
function notehead(
  ctx: CanvasRenderingContext2D,
  note: Notehead,
  size: number,
  colour: string,
  alpha: number,
) {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = colour;
  const h = size / 2;
  if (note.kind === "square") {
    ctx.fillRect(note.x - h, note.y - h, size, size);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(note.x, note.y - h);
  ctx.lineTo(note.x + h, note.y);
  ctx.lineTo(note.x, note.y + h);
  ctx.lineTo(note.x - h, note.y);
  ctx.closePath();
  ctx.fill();
}

export function initNotation() {
  const style = getComputedStyle(document.documentElement);
  const accent = style.getPropertyValue("--color-accent").trim() || "#17171a";
  const quiet = style.getPropertyValue("--color-ink-3").trim() || "#5d5b57";
  const alphas = [0.16, 0.34, 0.62];

  for (const canvas of document.querySelectorAll<HTMLCanvasElement>("[data-notation]")) {
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;

    const sparse = canvas.dataset.notationVariant === "sparse";
    const lineGap = sparse ? 18 : 10;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const field = staveField(
      Number(canvas.dataset.notation) || 1,
      canvas.width,
      canvas.height,
      lineGap,
      sparse ? 0.34 : 0.46,
    );

    // The rules first, so noteheads sit on top of them the way ink does.
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = quiet;
    for (const top of field.staves) {
      for (let i = 0; i < 4; i++) {
        ctx.fillRect(0, Math.round(top + i * lineGap), canvas.width, 1);
      }
    }

    for (const note of field.notes) {
      notehead(ctx, note, lineGap * 0.72, note.weight === 0 ? quiet : accent, alphas[note.weight]!);
    }
    ctx.globalAlpha = 1;
  }
}

/* ---- band texture: staves behind dark bands ---- */
export function initTextures() {
  const style = getComputedStyle(document.documentElement);
  const colour = style.getPropertyValue("--color-on-band").trim() || "#f5f4f2";

  for (const canvas of document.querySelectorAll<HTMLCanvasElement>("[data-notation-texture]")) {
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;

    // Size to the rendered box so noteheads stay square instead of stretching.
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (!width || !height) continue;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    ctx.scale(ratio, ratio);

    const lineGap = 20;
    const field = staveField(
      Number(canvas.dataset.notationTexture) || 1,
      width,
      height,
      lineGap,
      0.3,
    );

    ctx.globalAlpha = 0.07;
    ctx.fillStyle = colour;
    for (const top of field.staves) {
      for (let i = 0; i < 4; i++) ctx.fillRect(0, Math.round(top + i * lineGap), width, 1);
    }
    for (const note of field.notes) {
      notehead(ctx, note, lineGap * 0.7, colour, note.weight === 0 ? 0.08 : 0.14);
    }
    ctx.globalAlpha = 1;
  }
}

/* ---- contact form: submit without losing the page ---- */
export function initContactForm() {
  const form = document.querySelector<HTMLFormElement>("[data-contact-form]");
  if (!form) return;
  const status = form.querySelector<HTMLElement>("[data-form-status]");
  const button = form.querySelector<HTMLButtonElement>("button[type=submit]");

  const say = (key: "sending" | "sent" | "failed") => {
    if (status) status.textContent = status.dataset[key] ?? "";
  };

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;

    if (button) button.disabled = true;
    say("sending");

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(Object.fromEntries(new FormData(form))),
      });
      if (!response.ok) throw new Error(String(response.status));
      form.reset();
      say("sent");
    } catch {
      say("failed");
    } finally {
      if (button) button.disabled = false;
    }
  });
}

/**
 * Reveal and wire the copy buttons on install commands.
 *
 * They ship `hidden` because without the Clipboard API the button would be a
 * control that does nothing. Labels arrive as data attributes: this file
 * imports nothing, so it cannot read the dictionary.
 */
export function initCopyButtons() {
  if (!navigator.clipboard) return;
  const buttons = document.querySelectorAll<HTMLButtonElement>("button[data-copy]");
  for (const button of buttons) {
    button.hidden = false;
    button.addEventListener("click", async () => {
      const command = button.dataset.copy;
      if (!command) return;
      try {
        await navigator.clipboard.writeText(command);
      } catch {
        return;
      }
      const done = button.dataset.copied;
      if (!done) return;
      const original = button.textContent;
      button.textContent = done;
      setTimeout(() => {
        button.textContent = original;
      }, 1500);
    });
  }
}

/**
 * Theme toggle.
 *
 * The button ships `hidden` because without script it would do nothing, and a
 * control that does nothing is worse than no control. The *theme* itself does
 * not depend on this: prefers-color-scheme already gives a dark reader a dark
 * page with no JavaScript at all. This only adds the override.
 *
 * The stored value is read in a tiny blocking script in the head, not here —
 * by the time this module runs the page has painted, and a reader who chose
 * dark would see a white flash first.
 */
export function initTheme() {
  const button = document.querySelector<HTMLButtonElement>("[data-theme-toggle]");
  if (!button) return;

  const root = document.documentElement;
  const dark = () =>
    root.dataset.theme === "dark" ||
    (root.dataset.theme !== "light" && window.matchMedia("(prefers-color-scheme: dark)").matches);

  const word = button.querySelector<HTMLElement>("[data-theme-word]");

  const label = () => {
    // Everything here names what the click will DO, not what is on screen now.
    // Writing textContent would have been simpler and would also have deleted
    // the two glyphs the button is made of.
    const next = dark() ? (button.dataset.toLight ?? "") : (button.dataset.toDark ?? "");
    if (word) word.textContent = next;
    button.setAttribute("aria-label", next);
    button.setAttribute("aria-pressed", String(dark()));
    //
    // The glyphs are not touched from here. Which one shows follows the same
    // three-state CSS the colour tokens use, so it is already right before this
    // script runs and cannot drift from the theme it describes.
    //
    // Doing it in JavaScript went wrong twice, both times looking correct in
    // the DOM and wrong on screen: `hidden` is a reflected property of
    // HTMLElement and not of SVGElement, so `svg.hidden = true` set a plain
    // property that reflected nowhere — and React's types reject the attribute
    // on an SVG element for exactly that reason.
  };

  button.hidden = false;
  label();

  button.addEventListener("click", () => {
    const next = dark() ? "light" : "dark";
    root.dataset.theme = next;
    try {
      localStorage.setItem("theme", next);
    } catch {
      // Private mode, or storage disabled. The choice still applies to this page.
    }
    label();
  });

  // Follow the system while no explicit choice has been made.
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (root.dataset.theme === undefined) label();
  });
}

initNav();
initNotation();
initTextures();
initContactForm();
initCopyButtons();
initTheme();

// Textures depend on layout, so redraw when the box changes.
let resizeTimer: ReturnType<typeof setTimeout>;
window.addEventListener("resize", () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(initTextures, 150);
});
